import { auth0ApiClient } from './auth0ApiClient'
import {
  Message,
  Conversation,
  SendMessageRequest
} from '@/types/message'

interface ConversationParams {
  limit?: number
  skip?: number
}

interface ConversationResponse {
  messages: Message[]
  total: number
  hasMore: boolean
}

class MessageService {
  private readonly baseUrl = '/messages'

  /**
   * Send a message in a relationship
   */
  async sendMessage(data: SendMessageRequest): Promise<Message> {
    return auth0ApiClient.post<Message>(`${this.baseUrl}/send`, data)
  }

  /**
   * Get all conversations for the current user
   */
  async getConversations(): Promise<Conversation[]> {
    return auth0ApiClient.get<Conversation[]>(`${this.baseUrl}/conversations`)
  }

  /**
   * Get messages for a specific conversation/relationship
   */
  async getConversation(
    relationshipId: string,
    params?: ConversationParams
  ): Promise<ConversationResponse> {
    const queryString = params ? this.buildQueryString(params) : ''
    return auth0ApiClient.get<ConversationResponse>(
      `${this.baseUrl}/conversation/${relationshipId}${queryString}`
    )
  }

  /**
   * Mark a specific message as read
   */
  async markMessageAsRead(messageId: string): Promise<void> {
    return auth0ApiClient.put<void>(`${this.baseUrl}/${messageId}/read`, {})
  }

  /**
   * Mark all messages in a conversation as read
   */
  async markConversationAsRead(relationshipId: string): Promise<void> {
    return auth0ApiClient.put<void>(
      `${this.baseUrl}/conversation/${relationshipId}/read`,
      {}
    )
  }

  /**
   * Build query string from parameters
   */
  private buildQueryString(params: ConversationParams): string {
    const searchParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString())
      }
    })

    const queryString = searchParams.toString()
    return queryString ? `?${queryString}` : ''
  }
}

export const messageService = new MessageService()
